/**
 * Pure mapping from the server hospital row (M3-T02 shape) to the props the
 * `HospitalCard` atom wants. No React — kept here so it's unit-testable and the
 * screen stays declarative.
 */
import type { HospitalCardBed } from '../components/HospitalCard'
import type { BedState, HospitalListItem, ServiceFlags } from '../api/hospitals'

/** Availability tone for a bed type. Mirrors the admin bed table's rule
 * (`designs/screens-local.jsx` AdminBedUpdate): full → bad, < 15 % → warn,
 * else good. `total === 0` (the hospital doesn't offer the type) → bad/full. */
export function bedTone(free: number, total: number): HospitalCardBed['tone'] {
  if (total === 0 || free === 0) return 'bad'
  return free / total < 0.15 ? 'warn' : 'good'
}

/** The bed badges shown on a card: ICU, General, Maternity — the three a
 * patient scans first. Each pairs a colour with its free/total text. */
export function bedBadges(beds: BedState): HospitalCardBed[] {
  return [
    { label: 'ICU', free: beds.icu_free, total: beds.icu_total },
    { label: 'Gen', free: beds.general_free, total: beds.general_total },
    { label: 'Mat', free: beds.maternity_free, total: beds.maternity_total },
  ].map((b) => ({ ...b, tone: bedTone(b.free, b.total) }))
}

// Capability flag → short card label, in display order. Only `has_*` service
// flags map to a tag; the 24hr/night/fast flags refine filtering, not the tag.
const SERVICE_LABELS: ReadonlyArray<[keyof ServiceFlags, string]> = [
  ['has_emergency', 'Emergency'],
  ['has_pharmacy', 'Drugs'],
  ['has_imaging', 'Imaging'],
  ['has_surgery', 'Surgery'],
  ['has_specialists_onsite', 'Specialists'],
  ['has_lab', 'Labs'],
  ['has_chronic_care', 'Chronic care'],
  // No 'Ambulance' tag (M34-T05, ADR-0063, owner ruling Q8). The flag is
  // placeholder-quality, and "Ambulance" on a card beside Medcor Transport
  // invites the reading that Medcor sends one. `has_ambulance` stays in the
  // data and the API (and the staff screens); customers just don't see it.
]

/** Up to `max` service tags for a card, in priority order. */
export function serviceTags(flags: ServiceFlags, max = 3): string[] {
  return SERVICE_LABELS.filter(([key]) => flags[key])
    .map(([, label]) => label)
    .slice(0, max)
}

/** A hospital matches the search text if the query (case-insensitive) is a
 * substring of its name, city, or address. Empty query matches everything. */
export function matchesSearch(item: HospitalListItem, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    item.name.toLowerCase().includes(q) ||
    item.city.toLowerCase().includes(q) ||
    item.address.toLowerCase().includes(q)
  )
}
